import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../app/store';
import CampaignMetrics from './CampaignMetrics';
import CampaignTable from './CampaignTable';
import HealthScore from './HealthScore';
import QuickActions from './QuickActions';
import RecentActivities from './RecentActivities';
import TopCards from './TopCards';
// import dashcampaignpic from '../../assets/dashcampaignpic.png';
import { getAuthContext } from '../../utils/jwt';
import { BASE_URL } from '../../utils/constants';
import { toast } from 'react-toastify';

const Dashboard: React.FC = () => {
  const [isRefreshing, setIsRefreshing] = useState(false);

  const [topcardsData, setTopcardsData] = useState<any>(null);
  const [campaignMetricsData, setCampaignMetricsData] = useState<any>(null);
  const [campaignTableData, setCampaignTableData] = useState<any>(null);
  const [healthScoreData, setHealthScoreData] = useState<any>(null);
  const [recentactivitiesData, setRecentactivitiesData] = useState<any[]>([]);
  const [tenantId, setTenantId] = useState<string | null>(null);
  const [userId, setUserId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // Separate time ranges for each component
  const [metricsTimeRange, setMetricsTimeRange] = useState('last-30-days');
  // Default to '1' for Last 1 Month
  const [tableTimeRange, setTableTimeRange] = useState('1');

  // ✅ Check for new login flag and reload
  useEffect(() => {
    const isNewLogin = sessionStorage.getItem('is_new_login');

    if (isNewLogin === 'true') {
      // Clear the flag immediately to prevent infinite loop
      sessionStorage.removeItem('is_new_login');

      setIsRefreshing(true);

      // Reload the page after 1 second to fetch fresh data
      const timer = setTimeout(() => {
        window.location.reload();
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, []);

  // Decode token once using common utility
  useEffect(() => {
    const { tenantId: decodedTenantId, userId: decodedUserId } = getAuthContext();

    setTenantId(decodedTenantId);
    setUserId(decodedUserId || null);
  }, []);

  // Fetch metrics data when metricsTimeRange changes
  useEffect(() => {
    if (!tenantId) return;

    const fetchMetricsData = async () => {
      const { token } = getAuthContext();
      if (!token) return;

      try {
        const headers: Record<string, string> = {
          'X-Tenant-Id': tenantId,
          'Authorization': `Bearer ${token}`,
        };

        if (userId) {
          headers['X-User-Id'] = userId;
        }

        const metricsRes = await fetch(
          `${BASE_URL}/v1/dashboard/campaign-metrics-series?range=${metricsTimeRange}`,
          { headers }
        );

        if (metricsRes.ok) {
          const data = await metricsRes.json();
          setCampaignMetricsData(data);
        }
      } catch (error) {
        // console.error('Failed to fetch metrics data:', error);
        toast.error('Failed to load campaign metrics. Please try again.')
      }
    };

    fetchMetricsData();
  }, [tenantId, userId, metricsTimeRange]);

  // Fetch table data when tableTimeRange changes
  useEffect(() => {
    if (!tenantId) return;

    const fetchTableData = async () => {
      const { token } = getAuthContext();
      if (!token) return;

      try {
        const headers: Record<string, string> = {
          'X-Tenant-Id': tenantId,
          'Authorization': `Bearer ${token}`,
        };

        if (userId) {
          headers['X-User-Id'] = userId;
        }

        const tableRes = await fetch(
          `${BASE_URL}/v1/dashboard/campaign-table?months=${tableTimeRange}`,
          { headers }
        );

        if (tableRes.ok) {
          const data = await tableRes.json();
          setCampaignTableData(data);
        }
      } catch (error) {
        // console.error('Failed to fetch table data:', error);
        toast.error('Failed to load campaign table data. Please try again.');
      }
    };

    fetchTableData();
  }, [tenantId, userId, tableTimeRange]);

  // Fetch common data (overview, health, recent activities)
  useEffect(() => {
    if (!tenantId) return;

    const fetchCommonData = async () => {
      const { token } = getAuthContext();
      if (!token) {
        setLoading(false);
        return;
      }

      try {
        const headers: Record<string, string> = {
          'X-Tenant-Id': tenantId,
          'Authorization': `Bearer ${token}`,
        };

        if (userId) {
          headers['X-User-Id'] = userId;
        }

        const [overviewRes, healthRes, recentRes] = await Promise.all([
          fetch(`${BASE_URL}/v1/dashboard/overview`, { headers }),
          fetch(`${BASE_URL}/v1/dashboard/email-health`, { headers }),
          fetch(`${BASE_URL}/v1/dashboard/recent-activities`, { headers })
        ]);

        if (overviewRes.ok) setTopcardsData(await overviewRes.json());
        if (healthRes.ok) setHealthScoreData(await healthRes.json());
        if (recentRes.ok) {
          const data = await recentRes.json();
          setRecentactivitiesData(data.items || []);
        }
      } catch (error) {
        // console.error('Failed to fetch common data:', error);
        toast.error('Failed to load dashboard data. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchCommonData();
  }, [tenantId, userId]);

  // Handle metrics time range change
  const handleMetricsTimeRangeChange = (range: string) => {
    setMetricsTimeRange(range);
  };

  // Handle table time range change
  const handleTableTimeRangeChange = (range: string) => {
    setTableTimeRange(range);
  };

  // Campaign Metrics 
  // const campaignData2 = {
  //   "range": "last-30-days",
  //   "points": [
  //     { "date": "2026-07-16", "emailsSent": 1500, "openRate": 45.0, "clickRate": 15.0, "ctor": 33.0 },
  //     { "date": "2026-07-15", "emailsSent": 1450, "openRate": 47.0, "clickRate": 16.0, "ctor": 34.0 },
  //     { "date": "2026-07-14", "emailsSent": 1400, "openRate": 43.0, "clickRate": 14.0, "ctor": 32.0 },
  //     { "date": "2026-07-13", "emailsSent": 1350, "openRate": 46.0, "clickRate": 15.0, "ctor": 33.0 },
  //     { "date": "2026-07-12", "emailsSent": 1300, "openRate": 44.0, "clickRate": 13.0, "ctor": 30.0 },
  //     { "date": "2026-07-11", "emailsSent": 1250, "openRate": 48.0, "clickRate": 17.0, "ctor": 35.0 },
  //     { "date": "2026-07-10", "emailsSent": 1200, "openRate": 42.0, "clickRate": 12.0, "ctor": 28.0 },
  //     { "date": "2026-07-09", "emailsSent": 1150, "openRate": 45.0, "clickRate": 15.0, "ctor": 33.0 },
  //     { "date": "2026-07-08", "emailsSent": 1100, "openRate": 46.0, "clickRate": 16.0, "ctor": 34.0 },
  //     { "date": "2026-07-07", "emailsSent": 1050, "openRate": 44.0, "clickRate": 14.0, "ctor": 32.0 },
  //     { "date": "2026-07-06", "emailsSent": 1000, "openRate": 47.0, "clickRate": 15.0, "ctor": 33.0 },
  //     { "date": "2026-07-05", "emailsSent": 950, "openRate": 43.0, "clickRate": 13.0, "ctor": 30.0 },
  //     { "date": "2026-07-04", "emailsSent": 900, "openRate": 45.0, "clickRate": 16.0, "ctor": 35.0 }
  //   ]
  // };

  // TopCards data
  // const topcardsData2 = {
  //   "hasAnyCampaigns": true,
  //   "range": "last-30-days",
  //   "lastSentCampaign": {
  //     "campaignId": "CMP65912a826f194c61",
  //     "title": "Daily news – Language",
  //     "status": "COMPLETED",
  //     "sendMode": "SCHEDULE",
  //     "sentAt": "2026-04-01T22:47:00Z",
  //     "recipients": 8,
  //     "openRate": 66.67,
  //     "clickRate": 66.67,
  //     "ctor": 66.67,
  //     "thumbnailUrl": dashcampaignpic
  //   },
  //   "engagementGauge": {
  //     "score": 70,
  //     "label": "GOOD",
  //     "tenantOpenRate": 66.67,
  //     "industryOpenRate": 56.67,
  //     "deltaVsIndustry": 10.0
  //   },
  //   "metrics": {
  //     "totalCampaignsSent": 1,
  //     "totalEmailsSent": 8,
  //     "totalOpens": 8,
  //     "totalClicks": 8,
  //     "totalUnsubscribes": 0,
  //     "totalComplaints": 0,
  //     "avgOpenRate": 66.67,
  //     "avgClickRate": 66.67
  //   }
  // };

  // Empty state for TopCards
  // const topcardsDataEmpty = {
  //   "hasAnyCampaigns": false,
  //   "range": "last-30-days",
  //   "lastSentCampaign": {
  //     "campaignId": null,
  //     "title": null,
  //     "status": null,
  //     "sendMode": null,
  //     "sentAt": null,
  //     "recipients": 0,
  //     "openRate": 0.0,
  //     "clickRate": 0.0,
  //     "ctor": 0.0,
  //     "thumbnailUrl": null
  //   },
  //   "engagementGauge": {
  //     "score": 0,
  //     "label": null,
  //     "tenantOpenRate": 0.0,
  //     "industryOpenRate": 0.0,
  //     "deltaVsIndustry": 0.0
  //   },
  //   "metrics": {
  //     "totalCampaignsSent": 0,
  //     "totalEmailsSent": 0,
  //     "totalOpens": 0,
  //     "totalClicks": 0,
  //     "totalUnsubscribes": 0,
  //     "totalComplaints": 0,
  //     "avgOpenRate": 0.0,
  //     "avgClickRate": 0.0
  //   }
  // };

  // Health Score data
  // const healthScoreDataFinal2 = {
  //   totalSubscribers: 15240,
  //   activeSubscribers: 12000,
  //   newThisMonth: 2000,
  //   newToday: 500,
  //   unsubscribed: 740,
  //   deltaSinceLastMonthPercent: 8.0
  // };

  // Campaign Table data
  // const campaigntableData2 = {
  //   fromMonth: "2026-04",
  //   toMonth: "2026-01",
  //   rows: [
  //     {
  //       month: "2026-04",
  //       campaigns: 12,
  //       emailsSent: 2450,
  //       opened: { count: 892, rate: 36.4 },
  //       clicked: 654,
  //       unsubscribed: 23,
  //       complaints: 4
  //     },
  //     {
  //       month: "2026-03",
  //       campaigns: 18,
  //       emailsSent: 3780,
  //       opened: { count: 1587, rate: 42.0 },
  //       clicked: 1240,
  //       unsubscribed: 31,
  //       complaints: 7
  //     },
  //     {
  //       month: "2026-02",
  //       campaigns: 9,
  //       emailsSent: 1840,
  //       opened: { count: 920, rate: 50.0 },
  //       clicked: 680,
  //       unsubscribed: 12,
  //       complaints: 2
  //     },
  //     {
  //       month: "2026-01",
  //       campaigns: 15,
  //       emailsSent: 3120,
  //       opened: { count: 1092, rate: 35.0 },
  //       clicked: 875,
  //       unsubscribed: 45,
  //       complaints: 9
  //     }
  //   ]
  // };

  // Recent Activities data
  // const recentactivitiesData2 = [
  //   {
  //     id: 1,
  //     activity: "Campaign \"second campaign draft\" created",
  //     time: "Just now",
  //     badgeColor: "#7A52CCCC",
  //     textColor: "#7C3AED",
  //     icon: "📤"
  //   },
  //   {
  //     id: 2,
  //     activity: "Campaign sending started",
  //     time: "10 minutes ago",
  //     badgeColor: "#52CCB866",
  //     textColor: "#16A34A",
  //     icon: "📤"
  //   }
  // ];

  const campaignData = campaignMetricsData?.points?.map((point: any) => ({
    date: new Date(point.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    emailSent: point.emailsSent,
    openRate: point.openRate,
    clickRate: point.clickRate,
    ctor: point.ctor,
  })) || [];

  const campaigntableData = campaignTableData || { fromMonth: "", toMonth: "", rows: [] };

  const healthScoreDataFinal = healthScoreData || {
    totalSubscribers: 0, activeSubscribers: 0, newThisMonth: 0,
    newToday: 0, unsubscribed: 0, deltaSinceLastMonthPercent: 0.0,
  };

  const profileData = useSelector((state: RootState) => state.profile);
  const savedProfileData = localStorage.getItem('profileData');
  const userName = savedProfileData
    ? JSON.parse(savedProfileData).firstName
    : profileData.firstName || 'User';

  // ✅ Show refresh state
  if (isRefreshing) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-orange-500 mx-auto"></div>
          <p className="mt-4 text-gray-600 text-lg">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className='p-2'>
      <div className='text-2xl font-semibold text-gray-800 mb-4' style={{ color: '#3F5265', fontSize: '32px' }}>
        Welcome back, {userName}! 👋
      </div>
      <p className='text-gray-600 mb-6' style={{ color: '#657584' }}>
        Let's dive into your personalised setup guide.
      </p>

      <div className='grid grid-cols-1 md:grid-cols-4 gap-6'>
        <div className='md:col-span-3'>
          <TopCards dummyData={topcardsData} loading={loading} />
          <CampaignMetrics
            campaignData={campaignData}
            timeRange={metricsTimeRange}
            onTimeRangeChange={handleMetricsTimeRangeChange}
          />
          <CampaignTable
            campaigntableData={campaigntableData}
            timeRange={tableTimeRange}
            onTimeRangeChange={handleTableTimeRangeChange}
          />
        </div>

        <div className='md:col-span-1'>
          <RecentActivities recentactivitiesData={recentactivitiesData} />
          <QuickActions />
          <HealthScore healthScoreData={healthScoreDataFinal} />
        </div>
      </div>
    </div>
  );
};

export default Dashboard;